import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isRenewalWindow } from '@tfa/shared'
import { api } from '../services/api'
import { useTelegram } from '../hooks/useTelegram'

interface ProgramRenewalProps {
  /** Конец доступа (Client.subscriptionEnd). */
  accessEnd: string | null | undefined
  /** Ссылки продления его программы; null — в админке не заведены, кнопки нет. */
  links: { url: string; autoUrl: string | null } | null | undefined
}

/**
 * #602: «Продлить» программу — за неделю до конца доступа и после него.
 *
 * Кнопка ведёт на оплату продления именно его программы (ссылка из привязки
 * тарифа в админке), рядом — вариант с автопродлением, если он заведён. Когда
 * человек возвращается в мини-апп, тянем его лид из Акселя сразу: продлённый
 * доступ должен открыться тут же, а не на следующем плановом синке.
 */
export function ProgramRenewal({ accessEnd, links }: ProgramRenewalProps) {
  const { openLink, impactHaptic } = useTelegram()
  const queryClient = useQueryClient()
  const awaitingReturn = useRef(false)

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible' || !awaitingReturn.current) return
      awaitingReturn.current = false
      api.refreshXlAccess()
        .catch(() => undefined)
        .finally(() => queryClient.invalidateQueries({ queryKey: ['user', 'me'] }))
    }
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('focus', onReturn)
    return () => {
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('focus', onReturn)
    }
  }, [queryClient])

  if (!links?.url || !isRenewalWindow(accessEnd)) return null

  const end = new Date(accessEnd as string)
  const ended = end.getTime() <= Date.now()
  const date = end.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' })

  const go = (url: string) => {
    impactHaptic('medium')
    awaitingReturn.current = true
    openLink(url)
  }

  return (
    <div
      data-testid="program-renewal"
      style={{
        margin: '8px 16px 0',
        padding: '12px 14px',
        borderRadius: 14,
        background: ended ? 'rgba(225,112,85,0.12)' : 'rgba(108,92,231,0.10)',
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1C2D41' }}>
        {ended ? 'Доступ к программе закончился' : `Доступ к программе до ${date}`}
      </div>
      <div style={{ fontSize: 13, color: '#6B7B8D', marginTop: 2 }}>
        Продлите, чтобы продолжить тренировки без перерыва.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="program-renewal-pay"
          onClick={() => go(links.url)}
          style={{
            flex: '1 1 140px', border: 'none', borderRadius: 10, padding: '10px 12px',
            background: '#6C5CE7', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Продлить
        </button>
        {links.autoUrl && (
          <button
            type="button"
            data-testid="program-renewal-auto"
            onClick={() => go(links.autoUrl as string)}
            style={{
              flex: '1 1 140px', borderRadius: 10, padding: '10px 12px',
              border: '1.5px solid #6C5CE7', background: 'transparent', color: '#6C5CE7',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            С автопродлением
          </button>
        )}
      </div>
    </div>
  )
}
